import React, { useEffect, useState } from 'react'
import {
  CreditCardOutlined,
  QrcodeOutlined,
  CheckOutlined,
  CloseOutlined,
  SyncOutlined,
  LoadingOutlined,
} from '@ant-design/icons'
import { Button, Card, Typography, message } from 'antd'
import { useApiPolling } from './useApiPolling'
import POSService from '../../../core/services/POSService'
import { useTranslation } from 'react-i18next'
import Select from 'react-select'
import ImageWithBasePath from '../../../components/general/ImageCustom'
const { Title, Text } = Typography

const PaymentComponent = ({
  orderId,
  handleShowQR,
  addNewOrder,
  handleComletePaymentWithQR,
  posMachines,
  posId,
  orderDetail,
  totalPaymentAmount,
}) => {
  const [qrSentloading, setQrSentloading] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState(null)
  const [selectedPosMachine, setSelectedPosMachine] = useState()
  const [posMachinesList, setPosMachineList] = useState([])
  const { t } = useTranslation()

  const loadOptionsCustomers = () => {
    return posMachines.map((posMachine) => ({
      value: posMachine,
      label: `${posMachine.name}`,
    }))
  }

  useEffect(() => {
    POSService.getPosMachineById(posId).then((res) => {
      setSelectedPosMachine({
        value: res.records[0],
        label: `${res.records[0].name}-${t('default_pos_machine')}`,
      })
      setPosMachineList(
        posMachines.map((posMachine) => ({
          value: posMachine,
          label:
            res.records[0].id === posMachine.id
              ? `${posMachine.name}-${t('default_pos_machine')}`
              : `${posMachine.name}`,
        }))
      )
    })
  }, posMachines)

  const handleSentCardPayment = async () => {
    let orderData = {
      orderId: orderDetail.id,
      requestId: orderDetail.pos_reference,
      amount: totalPaymentAmount,
    }
    POSService.sendOrderToPOSCardPayment(orderData, selectedPosMachine.value).then((res) => {
      startPolling()
    })
  }

  const handlePaymentSuccess = async (paymentData) => {
    await handleComletePaymentWithQR().then((res) => {
      addNewOrder()
      message.success('Payment Successful!')
    })
    localStorage.removeItem('reloadProcessed')
    localStorage.setItem(
      'qrPos',
      JSON.stringify({
        show_qr_pos: false,
      })
    )
    window.dispatchEvent(new Event('localStorageChanged'))
    console.log('Payment successful:', paymentData)
  }

  const handlePaymentFailed = (paymentData) => {
    message.error('Payment Failed')
    console.log('Payment failed:', paymentData)
  }

  const [errorMessage, setErrorMessage] = useState(null)

  const { data, loading, startPolling, stopPolling } = useApiPolling(
    async () => {
      try {
        const response = await POSService.getOrderDetail(orderId)
        return response[0]
      } catch (err) {
        throw err
      }
    },
    (order) => {
      if (order.state === 'paid') {
        handlePaymentSuccess(order)
        return true
      }
      if (['FAILED', 'CANCELLED', 'REJECTED'].includes(order.state)) {
        handlePaymentFailed(order)
        return true
      }
      return false
    },
    {
      interval: 5000,
      maxAttempts: 20,
      onError: (err) => {
        setErrorMessage(err.message || 'An error occurred during payment verification')
      },
    }
  )

  const [qr, setQr] = useState(null)

  const handlePaymentMethodSelection = (method) => {
    setPaymentMethod(method)
  }

  const renderPaymentMethodSelection = () => (
    <>
      <div className='inline-flex items-center justify-start gap-1 self-stretch'>
        <div className="font-['Inter Display'] text-base font-normal leading-normal text-[#525866]">
          {t('choose_payment_method')}
        </div>
      </div>
      <div
        className='flex flex-col items-start justify-start gap-4 self-stretch overflow-hidden rounded-2xl border border-neutral-200 bg-white p-3 transition-all duration-300 hover:scale-[1.02] hover:cursor-pointer hover:shadow-lg'
        onClick={() => handlePaymentMethodSelection('card')}
      >
        <div className='inline-flex items-center justify-start gap-2 self-stretch'>
          <div className='inline-flex h-9 w-9 flex-col items-center justify-center gap-[6.43px] overflow-hidden rounded-[10.29px] bg-[#28c76f]/10'>
            <div className='relative h-6 w-6'>
              <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path
                  id='Vector'
                  d='M0 12C0 5.37208 5.37262 0 11.9998 0C18.6274 0 24 5.37213 24 12C24 18.6272 18.6274 23.9998 11.9998 23.9998C5.37262 23.9998 0 18.6272 0 12Z'
                  fill='#28C76F'
                />
              </svg>

              <div className='absolute left-[5px] top-[5px] inline-flex h-3.5 w-3.5 items-center justify-center'>
                <ImageWithBasePath src='assets/img/POS/white-card.svg' alt='' />
              </div>
            </div>
          </div>
          <div className="font-['Inter Display'] text-base font-medium leading-normal text-[#424653]">
            {t('card_payment')}
          </div>
        </div>
      </div>
      <div
        className='flex flex-col items-start justify-start gap-4 self-stretch overflow-hidden rounded-2xl border border-neutral-200 bg-white p-3 transition-all duration-300 hover:scale-[1.02] hover:cursor-pointer hover:shadow-lg'
        onClick={() => handlePaymentMethodSelection('qr')}
      >
        <div
          className='inline-flex items-center justify-start gap-2 self-stretch'
          onClick={() => {
            setQrSentloading(true)
            setQr(null)
            handleShowQR().then((res) => {
              if (res) {
                setQr(true)
                startPolling()
              } else {
                setQr(false)
              }
            })
          }}
        >
          <div className='inline-flex h-9 w-9 flex-col items-center justify-center gap-[6.43px] overflow-hidden rounded-[10.29px] bg-[#005aa9]/10'>
            <div className='relative h-6 w-6'>
              <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
                <path
                  id='Vector'
                  d='M0 12C0 5.37208 5.37262 0 11.9998 0C18.6274 0 24 5.37213 24 12C24 18.6272 18.6274 23.9998 11.9998 23.9998C5.37262 23.9998 0 18.6272 0 12Z'
                  fill='#005AA9'
                />
              </svg>

              <div className='absolute left-[5px] top-[5px] inline-flex h-3.5 w-3.5 items-center justify-center'>
                <ImageWithBasePath src='assets/img/POS/scan.svg' alt='' />
              </div>
            </div>
          </div>
          <div className="font-['Inter Display'] text-base font-medium leading-normal text-[#424653]">
            {t('qr_payment')}
          </div>
        </div>
      </div>
    </>
  )

  const renderQRPayment = () => (
    <Card>
      <Title level={4} className='mb-6 text-center'>
        {t('qr_payment')}
      </Title>
      <div className='flex w-full flex-col items-center'>
        <div className='mb-4 flex w-full justify-between'>
          <Button
            type='primary'
            icon={<QrcodeOutlined />}
            onClick={() => {
              setQrSentloading(true)
              setQr(null)
              handleShowQR().then((res) => {
                if (res) {
                  setQr(true)
                  startPolling()
                } else {
                  setQr(false)
                }
              })
            }}
            className='mr-2.5 bg-primary'
          >
            {t('show_qr_code')}
          </Button>

          {!loading ? (
            <Button type='primary' icon={<SyncOutlined />} onClick={startPolling} className='bg-primary'>
              {t('check_payment_status')}
            </Button>
          ) : (
            <Button type='primary' icon={<CloseOutlined />} onClick={stopPolling} className='bg-primary'>
              {t('stop_checking')}
            </Button>
          )}
        </div>

        {loading && (
          <div className='my-5 flex items-center justify-center text-center'>
            <LoadingOutlined className='mr-2.5 text-2xl' />
            <Text>Checking payment status...</Text>
          </div>
        )}

        {errorMessage && <div className='mb-4 text-red-500'>{errorMessage}</div>}

        {data?.state === 'paid' && !loading && (
          <div className='flex items-center text-green-500'>
            <CheckOutlined className='mr-2.5 text-2xl' />
            <Text strong>{t('payment_completed')}</Text>
          </div>
        )}

        {qrSentloading && qr == null && (
          <div className='my-5 flex items-center justify-center text-center'>
            <LoadingOutlined className='mr-2.5 text-2xl' />
            <Text>{t('generating_qr_code')}</Text>
          </div>
        )}

        {qr != null && !data?.state !== 'paid' && (
          <>
            {qr == true ? (
              <div className='flex w-full items-center justify-between text-green-500'>
                <CheckOutlined className='mr-2.5 text-2xl' />
                <Text className='mr-2.5' strong>
                  {t('qr_code_sent')}
                </Text>
                {!loading && (
                  <Button
                    type='primary'
                    icon={<CloseOutlined />}
                    onClick={() => {
                      stopPolling()
                      localStorage.removeItem('reloadProcessed')
                      localStorage.setItem(
                        'qrPos',
                        JSON.stringify({
                          show_qr_pos: false,
                        })
                      )
                      window.dispatchEvent(new Event('localStorageChanged'))
                      setQr(null)
                      setQrSentloading(false)
                    }}
                    danger
                  >
                    {t('hide_qr_code')}
                  </Button>
                )}
              </div>
            ) : (
              <div className='flex items-center text-red-500'>
                <CloseOutlined className='mr-2.5 text-2xl' />
                <Text strong>{t('qr_code_not_sent_try_again')}</Text>
              </div>
            )}
          </>
        )}

        <Button
          type='default'
          onClick={() => {
            setPaymentMethod(null)
            stopPolling()
          }}
          className='mt-4'
        >
          {t('change_payment_method')}
        </Button>
      </div>
    </Card>
  )

  const renderCardPayment = () => (
    <Card>
      <Title level={4} className='mb-6 text-center'>
        {t('card_payment')}
      </Title>

      <Select
        options={posMachinesList}
        placeholder={t('pos_machine')}
        value={selectedPosMachine}
        onChange={(posMachine) => {
          setSelectedPosMachine(posMachine)
        }}
        className='w-full'
        isSearchable={false}
      />

      <Button
        type='primary'
        size='large'
        icon={<CreditCardOutlined />}
        className='btn-primary mt-4 w-full'
        onClick={handleSentCardPayment}
      >
        {t('pay_now')}
      </Button>

      <Button
        type='default'
        onClick={() => {
          setPaymentMethod(null)
          stopPolling()
        }}
        className='mt-4 w-full'
      >
        {t('change_payment_method')}
      </Button>
    </Card>
  )

  return (
    <>
      {!paymentMethod && renderPaymentMethodSelection()}
      {paymentMethod === 'qr' && renderQRPayment()}
      {paymentMethod === 'card' && renderCardPayment()}
    </>
  )
}

export default PaymentComponent
